<script setup lang="ts">

import ItemV3ParameterBox from "@/components/itemV3/parameter/itemV3ParameterBox.vue";
import ItemV3ShowMore from "@/components/itemV3/parameter/itemV3ShowMore.vue";
import ItemEngraving from "@/components/itemEngraving.vue";
import {computed, inject, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";

const {t} = useI18n();

const emit = defineEmits(["patch", "update:engraving", "switch:image"]);
const props = defineProps(["item"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");
const patchMultiple = inject<any>("patchMultiple");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

// Item whose engraving is displayed/edited here. Defaults to the globally active item, but
// can be switched locally so this widget can show/edit another item's engraving without
// touching the injected itemIdx (which e.g. drives the "discs" list layout in itemV3DiscsColor).
const selectedItemIdx = ref<number>(itemIdx.value)

const items = computed(() => session.value?.items || [])

const itemSliderStyle = computed(() => {
  const count = items.value.length;
  if (count === 0) return {display: 'none'}

  const width = 100 / count;
  const left = selectedItemIdx.value * width;

  return {
    width: `calc(${width}% - 4px)`,
    left: `calc(${left}% + 2px)`,
  };
})

const selectItem = (idx: number) => {
  selectedItemIdx.value = idx
}

// Keep following the globally active item by default, without ever pushing the local
// selection back onto itemIdx.
watch(itemIdx, (newIdx) => {
  selectedItemIdx.value = newIdx
})

const imageWidthPercent: number = 10
const loading = ref(false)

/**
 * Parameter box header images
 */

const headerMainMetal = computed(() => store.getters["session/getMainMetal"])
const boxIdx = "engraving"

const getHeaderImage = (size: string = "600x300", withBg = false) => {
  if (withBg) {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${headerMainMetal.value}_${boxIdx}.webp`
  } else {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${boxIdx}.webp`
  }
}

/**
 * session
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])

// Tracks the box's own open/closed state (bound via v-model:open below) instead of a
// hardcoded "false", so it survives re-renders triggered by a patch - see
// itemV3ParameterBox.vue's onUpdated hook, which re-asserts this value on every update.
const open = ref(false)


/**
 * image
 */


/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};


/**
 * tip texts
 */

const tipTypeKeys: { [key in EngravingType]: string } = {
  LASER: "laser",
  HANDWRITING: "handwriting",
  FINGERPRINT: "fingerprint",
  OWN_DRAFT: "own_draft"
}

const hoveredType = ref<EngravingType | null>(null)

const previewType = computed<EngravingType>(() => hoveredType.value || selectedType.value)

const setHoveredType = (type: EngravingType) => {
  hoveredType.value = type
}

const clearHoveredType = () => {
  hoveredType.value = null
}

const getTipTitle = computed(() => {
  return t('components.parameters.tips.engraving.' + tipTypeKeys[previewType.value] + '.title')
})

const getTipText = computed(() => {
  return t('components.parameters.tips.engraving.' + tipTypeKeys[previewType.value] + '.text')
})

const getTipImageUrl = computed(() => {
  return 'https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/tips/engraving/' + tipTypeKeys[previewType.value] + '-600.webp'
})

/**
 * engraving
 */

const pageStr = "components.itemV2.itemV2Engraving"

const ENGRAVING_ASSET_BASE_URL = "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/engraving/"

const FOCUSED_FONT_KEYS = ["EnglischeSchT", "MonotypeCorsiva", "PalatinoLinotype"]
const FOCUSED_SYMBOL_COUNT = 5

const textInput = ref<HTMLElement[] | null>(null);

/**
 * engraving position (inside / outside / left / right)
 */

const ENGRAVING_POSITIONS = ["inside", "outside", "left", "right"] as const
type EngravingPosition = typeof ENGRAVING_POSITIONS[number]

const positionLabelKeys: { [key in EngravingPosition]: string } = {
  inside: "position_inside",
  outside: "position_outside",
  left: "position_left",
  right: "position_right"
}

const selectedPosition = ref<EngravingPosition>("inside")

const positionSliderStyle = computed(() => {
  const count = ENGRAVING_POSITIONS.length;
  const activeIdx = ENGRAVING_POSITIONS.indexOf(selectedPosition.value);
  const width = 100 / count;
  const left = activeIdx * width;

  return {
    width: `calc(${width}% - 4px)`,
    left: `calc(${left}% + 2px)`,
  };
})

// position of the small connector arrow that attaches the engraving type
// sub-selector to the currently selected position button
const positionCaretStyle = computed(() => {
  const count = ENGRAVING_POSITIONS.length;
  const activeIdx = ENGRAVING_POSITIONS.indexOf(selectedPosition.value);
  const width = 100 / count;
  const center = activeIdx * width + width / 2;

  return {
    left: `${center}%`,
  };
})

const selectPosition = (position: EngravingPosition) => {
  if (position === selectedPosition.value) {
    return
  }

  selectedPosition.value = position
  clearHoveredFont()
  clearHoveredType()
}

const engraving = computed(() => session.value.engravings[selectedItemIdx.value][selectedPosition.value])

const mainMetal = computed(() => items.value[selectedItemIdx.value]?.image?.main_metal)

/**
 * personal data: item size (logic ported from itemV3PersonalData.vue), tied to whichever
 * item is currently selected via the item switch above.
 */

const sizeVariantIdx = "size"

const sizeParametersVariations = new ParametersVariations(sizeVariantIdx, false, session, selectedItemIdx, loadItemVariants)

const sizeVariants = computed(() => open.value ? sizeParametersVariations.getVariants() : [])

// Explicitly (re)load the "size" variant values for whichever item the user just switched to,
// since the previously selected item's values are cached on the session and won't refresh
// on their own when another item's data hasn't been fetched yet.
watch(selectedItemIdx, (idx) => {
  loadItemVariants(idx, sizeVariantIdx)
})

const sizeActiveIdx = ref(0)

const sizeVariantsList = computed(() => {
  if (!sizeVariants.value.length || sizeVariants.value.length < sizeActiveIdx.value) return []
  return sizeVariants.value[sizeActiveIdx.value].values
})

const sizeActive = computed(() => {
  if (!sizeVariants.value.length || sizeVariants.value.length < sizeActiveIdx.value) return []
  return sizeVariants.value[sizeActiveIdx.value]
})

const sizeMatchedIdx = computed(() => {
  if (!sizeVariants.value.length || sizeVariants.value.length < sizeActiveIdx.value) return 0
  return sizeVariants.value[sizeActiveIdx.value].values.findIndex((v: any) => v.match)
})

/**
 * size input (native number field with min/max/step 0.5, replacing the former slider) -
 * min/max mirror the available "size" variant values, so the native steppers always land
 * on a value the API accepts.
 */

const parseSizeValue = (value: any) => {
  const n = parseFloat(value?.idx)
  return isNaN(n) ? null : n
}

const sizeCurrentValue = computed(() => {
  if (!sizeVariantsList.value.length || sizeMatchedIdx.value < 0) return null
  return sizeVariantsList.value[sizeMatchedIdx.value]
})

const sizeMin = computed(() => sizeVariantsList.value.length ? (parseSizeValue(sizeVariantsList.value[0]) ?? undefined) : undefined)
const sizeMax = computed(() => sizeVariantsList.value.length ? (parseSizeValue(sizeVariantsList.value[sizeVariantsList.value.length - 1]) ?? undefined) : undefined)

// The editable size bound to the input
const sizeModel = ref<number | null>(null)

// Keep sizeModel in sync with the currently matched size value
watch(sizeCurrentValue, (value) => {
  sizeModel.value = parseSizeValue(value)
}, {immediate: true})

/**
 * Patch (size)
 *
 * Uses patchMultiple (instead of the regular @patch emit) so the size of the item currently
 * selected via the item switch is patched, even when it isn't the globally active item.
 */

const handleSizePatch = (actionIdx: string, value: any) => {
  patchMultiple([
    {itemIdx: selectedItemIdx.value, action: actionIdx, value: value.idx}
  ]);
};

// Commits the size input: snaps the typed value into the min/max range, then patches it
// using the matching variant value if one exists (so the exact idx/title the API expects is
// sent), falling back to the formatted raw number otherwise.
const commitSize = () => {
  if (!sizeActive.value || Array.isArray(sizeActive.value) || !sizeVariantsList.value.length) return

  if (sizeModel.value === null || isNaN(sizeModel.value)) {
    // Reset to the currently matched value if the field was cleared
    sizeModel.value = parseSizeValue(sizeCurrentValue.value)
    return
  }

  let value = sizeModel.value
  if (sizeMin.value !== undefined) value = Math.max(sizeMin.value, value)
  if (sizeMax.value !== undefined) value = Math.min(sizeMax.value, value)
  sizeModel.value = value

  const matched = sizeVariantsList.value.find((v: any) => parseSizeValue(v) === value)
  handleSizePatch(sizeActive.value.idx, matched || {idx: value.toFixed(1)})
}

/**
 * personal data: item name (logic ported from itemV3PersonalData.vue), tied to whichever
 * item is currently selected via the item switch above.
 */

const namePageStr = "components.itemV2.itemV2Name"

const persons = computed(() => session?.value?.persons ?? []);

// Determine which person a given item belongs to; falls back to the item's own index so an
// item without an explicit person association still gets a stable, distinct default name.
const findPersonIdxForItem = (idx: number) => {
  const found = persons.value.findIndex((p: any) => Array.isArray(p.items) && p.items.includes(idx));
  return found >= 0 ? found : idx;
};

// Display name for a given item in the item selection list below, falling back to the
// default person name when no name has been set yet.
const getItemName = (idx: number) => {
  const person = persons.value?.[findPersonIdxForItem(idx)];
  return person?.name || t(namePageStr + '.person_n', {n: idx + 1});
};

const selectedPersonIdx = ref(0);

// Ensure the currently selected item is listed for the selected person (frontend side)
const ensureItemIncluded = () => {
  const p = persons.value?.[selectedPersonIdx.value];
  if (!p) return;
  if (!Array.isArray(p.items)) p.items = [];
  if (!p.items.includes(selectedItemIdx.value)) {
    // Update locally so the UI reflects the association immediately
    p.items.push(selectedItemIdx.value);
    // Persist the association via patch so the backend knows this item belongs to the person
    emit('patch', { action: `person.${selectedPersonIdx.value}.items`, value: p.items, certificated_diamond: true });
  }
};

onMounted(() => {
  selectedPersonIdx.value = findPersonIdxForItem(selectedItemIdx.value);
  ensureItemIncluded();
});

watch(selectedItemIdx, (idx) => {
  selectedPersonIdx.value = findPersonIdxForItem(idx);
  ensureItemIncluded();
});

// The editable name bound to the input
const nameModel = ref("");

// Keep nameModel in sync with selected person
watch(
    () => [selectedPersonIdx.value, persons.value?.[selectedPersonIdx.value]?.name],
    () => {
      const p = persons.value?.[selectedPersonIdx.value];
      nameModel.value = p?.name ?? "";
    },
    {immediate: true}
);

// Emit patch on change as specified: action "person.<person_id>.name" with the name value
const commitName = () => {
  const personId = selectedPersonIdx.value;
  let value = (nameModel.value || "").trim();
  if (!value) {
    // If name is empty, store default name: "Item <person_id>"
    value = t(namePageStr + '.person_n', {n: personId + 1});
    // Reflect the default in the UI immediately
    nameModel.value = value;
  }
  emit('patch', { action: `person.${personId}.name`, value, certificated_diamond: true });
};

/**
 * engraving type (laser / handwriting / fingerprint / own draft)
 */

const SPECIAL_FONTS = ["HANDWRITING", "FINGERPRINT", "OWN_DRAFT"] as const
type SpecialFont = typeof SPECIAL_FONTS[number]
type EngravingType = "LASER" | SpecialFont

const typeLabelKeys: { [key in EngravingType]: string } = {
  LASER: "type_laser",
  HANDWRITING: "type_handwriting",
  FINGERPRINT: "type_fingerprint",
  OWN_DRAFT: "type_own_draft"
}

const isSpecialFontAllowed = computed(() => selectedPosition.value === "inside" || selectedPosition.value === "outside")

const engravingTypes = computed<EngravingType[]>(() => {
  if (!isSpecialFontAllowed.value) {
    return ["LASER"]
  }

  return ["LASER", "HANDWRITING", "FINGERPRINT", "OWN_DRAFT"]
})

// "Laser" is always the fallback type: whenever no special font is selected (or special
// fonts aren't allowed for the current position), the type defaults to "Laser" - regardless
// of whether the engraving text is currently empty.
const selectedType = computed<EngravingType>(() => {
  if (isSpecialFontAllowed.value && (SPECIAL_FONTS as readonly string[]).includes(engraving.value.font)) {
    return engraving.value.font as SpecialFont
  }

  return "LASER"
})

const typeSliderStyle = computed(() => {
  const count = engravingTypes.value.length;
  const activeIdx = engravingTypes.value.indexOf(selectedType.value);
  const width = 100 / count;
  const left = activeIdx * width;

  return {
    width: `calc(${width}% - 4px)`,
    left: `calc(${left}% + 2px)`,
  };
})

const isLaserSelected = computed(() => selectedType.value === "LASER")

const hoveredFont = ref<string | null>(null)

const previewFontFamily = computed(() => hoveredFont.value || engraving.value.font)

const setHoveredFont = (fontIdx: string) => {
  hoveredFont.value = fontIdx
}

const clearHoveredFont = () => {
  hoveredFont.value = null
}

// Switching between items inside the engraving box should reset the view back to the
// default position: "inside".
watch(selectedItemIdx, () => {
  clearHoveredFont()
  clearHoveredType()
  selectedPosition.value = "inside"
})

const templateFollowsText = computed(() => t(pageStr + ".template_follows"))

const selectType = (type: EngravingType) => {
  if (type === selectedType.value) {
    return
  }

  if (type === "LASER") {
    if (!engraving.value.font || (SPECIAL_FONTS as readonly string[]).includes(engraving.value.font)) {
      engraving.value.font = fontEntries.value[0][0]
    }

    engraving.value.text = ""

    updateFont()

    patchMultiple([
      {
        itemIdx: selectedItemIdx.value,
        action: 'engraving.' + selectedPosition.value + '.font',
        value: engraving.value.font
      },
      {
        itemIdx: selectedItemIdx.value,
        action: 'engraving.' + selectedPosition.value + '.text',
        value: engraving.value.text
      }
    ]);
  } else {
    engraving.value.font = type
    engraving.value.text = templateFollowsText.value

    updateFont()

    patchMultiple([
      {
        itemIdx: selectedItemIdx.value,
        action: 'engraving.' + selectedPosition.value + '.font',
        value: engraving.value.font
      },
      {
        itemIdx: selectedItemIdx.value,
        action: 'engraving.' + selectedPosition.value + '.text',
        value: engraving.value.text
      }
    ]);
  }
}

const engravingFonts: { [key: string]: string } = {
  "Amsterdam": "Amsterdam",
  "ArialSymbols": "Arial",
  "AvantGarde": "Avant Garde",
  "Benguiat": "Benguiat",
  "Berlin": "Berlin",
  "BoereTudor": "Boere Tudor",
  "EdwardianScriptITC": "Edwardian Script ITC (ID: 10)",
  "EnglischeSchT": "Englische SchT (ID: 3)",
  "English111VivaceBT": "English 111 Vivace BT (ID: 11)",
  "FreestyleScript": "Freestyle Script (ID: 16)",
  "PalaceScript": "Palace Script (ID: 4)",
  "Genf": "Genf",
  "Italic": "Italic",
  "Koeln": "Koeln",
  "LegerD": "LegerD (ID: 15)",
  "MonotypeCorsiva": "Monotype Corsiva (ID: 14)",
  "NuptialBT": "Nuptial BT",
  "Oslo": "Oslo",
  "PalatinoLinotype": "Palatino Linotype (ID: 62)",
  "Paris": "Paris",
  "ScriptMTBold": "Script MT Bold",
  "SloganD": "SloganD",
  "TimesNewRoman": "Times New Roman (ID: 2)",
  "Vivaldi": "Vivaldi (ID: 13)"
}

const engravingSymbols: { [key: string]: string } = {
  // a_Herz.dxf
  "heart": "Ⓐ",
  // b_Doppelherz.dxf
  "doubleHeart": "Ⓒ",
  // c_Doppelherz-m-Pfeil.dxf
  "doubleHeartArrow": "Ⓑ",
  // d_Herz-m-Pfreil.dxf
  "heartArrow": "Ⓓ",
  // e_Doppelringe.dxf
  "doubleRings": "Ⓘ",
  // f_Taube.dxf
  "pigeon": "Ⓙ",
  // g_Infinity-Zeichen.dxf
  "infinity": "Ⓕ",
  // h_Herzschloss.dxf
  "heartCastle": "Ⓚ",
  // i_Anker.dxf
  "anchor": "Ⓝ",
  // j_Glaube-Liebe-Hoffnung.dxf
  //"faith": "Ⓔ",
  // k_Amors-Bogen.dxf
  "amorArrow": "Ⓖ",
  // l_Amor.dxf
  "amor": "Ⓗ",
  // m_Krone.dxf
  "crown": "Ⓛ",
  // n_Kutsche.dxf
  //"carriage": "Ⓞ"
}

const fontEntries = computed(() => Object.entries(engravingFonts))
const symbolEntries = computed(() => Object.entries(engravingSymbols))

const focusedFontEntries = computed(() => FOCUSED_FONT_KEYS.map((fontIdx) => [fontIdx, engravingFonts[fontIdx]] as [string, string]))
const overflowFontEntries = computed(() => fontEntries.value.filter(([fontIdx]) => !(FOCUSED_FONT_KEYS as readonly string[]).includes(fontIdx)))

const focusedSymbolEntries = computed(() => symbolEntries.value.slice(0, FOCUSED_SYMBOL_COUNT))
const overflowSymbolEntries = computed(() => symbolEntries.value.slice(FOCUSED_SYMBOL_COUNT))

const formatSymbolName = (symboleIdx: string) => {
  const spaced = symboleIdx.replace(/([a-z])([A-Z])/g, '$1 $2')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const addSymbole = (symboleIdx: string) => {
  const pos = textInput.value?.selectionStart || 0
  const symbole = engravingSymbols[symboleIdx]

  engraving.value.text = engraving.value.text.slice(0, pos) + " " + symbole + " " + engraving.value.text.slice(pos);

  engraving.value.text = engraving.value.text.replace(/ +(?= )/g, '')

  updateFont()

  // showLoading=false: adding a symbol must not trigger the full-screen loading animation,
  // so the user can keep using the field and add more symbols right away.
  patchMultiple([
    {
      itemIdx: selectedItemIdx.value,
      action: 'engraving.' + selectedPosition.value + '.text',
      value: engraving.value.text
    }
  ], false);

}

const selectFont = (fontId: string) => {
  engraving.value.font = fontId;

  updateFont()

  // Cancel any pending debounced text commit (see commitEngravingText) - its text value is
  // sent together with the font below, so a stale later commit would be redundant.
  if (commitEngravingTextTimeout) {
    clearTimeout(commitEngravingTextTimeout)
    commitEngravingTextTimeout = null
  }

  // Always send the current text along with the font: patchMultiple replaces the whole local
  // session with the server's response, so if the text hasn't been committed to the backend yet
  // (e.g. the user just typed it and the debounced commit hasn't fired), omitting it here would
  // cause the server's stale value to wipe out the text the user just typed.
  patchMultiple([
    {
      itemIdx: selectedItemIdx.value,
      action: 'engraving.' + selectedPosition.value + '.font',
      value: engraving.value.font
    },
    {
      itemIdx: selectedItemIdx.value,
      action: 'engraving.' + selectedPosition.value + '.text',
      value: engraving.value.text.trim()
    }
  ]);
}

const updateFont = () => {
  engraving.value.text = engraving.value.text.trim()

  // Only forward the engraving object to the parent when editing the globally active item -
  // the parent assigns it to session.engravings[itemIdx], which must stay untouched for other items.
  if (selectedItemIdx.value === itemIdx.value) {
    emit('update:engraving', engraving.value);
  }
  emit('switch:image', 'Engraving')
}

const onTextInput = () => {
  if (selectedItemIdx.value === itemIdx.value) {
    emit('update:engraving', engraving.value);
  }
  emit('switch:image', 'Engraving')
}

// Delay between the text input losing focus and the text actually being sent to the backend.
// This gives the user enough time to click on a symbol right after typing/leaving the field -
// without the delay, the "change" event could send the text to the API before the symbol click
// is processed, so the newly added symbol would be missing from the saved text.
const COMMIT_ENGRAVING_TEXT_DELAY = 500

let commitEngravingTextTimeout: ReturnType<typeof setTimeout> | null = null

const commitEngravingText = () => {
  if (commitEngravingTextTimeout) {
    clearTimeout(commitEngravingTextTimeout)
  }

  commitEngravingTextTimeout = setTimeout(() => {
    commitEngravingTextTimeout = null

    // showLoading=false: committing the engraving text must not trigger the full-screen loading
    // animation, so the user can keep using the field and add more symbols without interruption.
    patchMultiple([
      {
        itemIdx: selectedItemIdx.value,
        action: 'engraving.' + selectedPosition.value + '.text',
        value: engraving.value.text.trim()
      }
    ], false);
  }, COMMIT_ENGRAVING_TEXT_DELAY)
}

</script>

<template>
  <item-v3-parameter-box
      v-model:open="open"
      :image-width-percent=imageWidthPercent
      :header-image="getHeaderImage()"
      :header-image-srcset="getHeaderImage() + ' 300w, ' + getHeaderImage('1200x600') +' 600w'"
      title="Gravur & Größe"
      subtitle="Wählen Sie die Gravur und die Ringgröße"
      :loading=loading
      :tip-title="getTipTitle"
      :tip-text="getTipText"
      :tip-image="getTipImageUrl"
  >
    <template v-slot:main>

      <div class="block space-y-4 w-full">
        <nav v-if="items.length > 1" class="w-full mb-4">
          <!-- item selection: switches which item's engraving is shown/edited here only; it
               never touches the global itemIdx used elsewhere (e.g. the discs list layout) -->
          <div
              class="relative flex items-center w-full h-[22px] max-h-[22px] bg-[#f3f0e9] rounded-full p-[2px] border border-[#e8e2d8] select-none">
            <!-- Sliding Background -->
            <div
                class="absolute top-[2px] bottom-[2px] bg-brand-main rounded-full transition-all duration-300 ease-in-out shadow-xs pointer-events-none"
                :style="itemSliderStyle"
            ></div>

            <!-- Item Buttons -->
            <button
                v-for="(ringItem, idx) in items"
                :key="idx"
                type="button"
                class=" text-[10px] tracking-wided relative flex-1 flex cursor-pointer items-center justify-center text-xs transition-colors duration-200 z-10 font-normal leading-none h-full"
                :class="selectedItemIdx === idx ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700'"
                @click="selectItem(idx)"
            >
              {{ getItemName(idx) }}
            </button>
          </div>
        </nav>
        <!-- combined name + size card, styled like the groove overview list items -->
        <div class="flex items-center gap-3 rounded-2xl p-2 bg-white border border-gray-200">
          <!-- item name icon -->
          <span class="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl text-brand-main">
            <img
                src="https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/engraving/300x300/item_name.webp"
                srcset="https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/engraving/600x600/item_name.webp"
                alt=""
            />
          </span>

          <div class="min-w-0 flex-1">
            <span class="block text-[10px] font-medium tracking-[0.1px] text-parameter-title mb-1.5">{{ t(namePageStr + '.article_for') }}</span>

            <div class="flex items-center gap-2">
              <input
                  type="text"
                  v-model="nameModel"
                  @change="commitName"
                  class="min-w-0 flex-1 text-sm rounded-xl bg-[#faf8f4] px-2.5 py-1.5 border border-[#e7ddcc] transition-colors focus:border-brand-main focus:outline-none"
              />
              <input
                  v-if="sizeVariantsList.length"
                  type="number"
                  v-model.number="sizeModel"
                  :min="sizeMin"
                  :max="sizeMax"
                  step="0.5"
                  @change="commitSize"
                  class="w-20 shrink-0 text-sm rounded-xl bg-[#faf8f4] px-2.5 py-1.5 border border-[#e7ddcc] transition-colors focus:border-brand-main focus:outline-none"
              />
              <!-- fake check button: not an actual submit, just forces both fields to commit -->
              <button
                  type="button"
                  class="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-brand-main transition-colors hover:bg-brand-main hover:text-white"
                  @click="commitName(); commitSize()"
                  :title="t(namePageStr + '.saved')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Engraving -->
        <nav class="w-full">
          <!-- position selection -->
          <div
              class="relative flex items-center w-full h-24 bg-[#f3f0e9] rounded-full p-[2px] border border-[#e8e2d8] select-none">
            <!-- Sliding Background -->
            <div
                class="absolute top-[2px] bottom-[2px] bg-brand-main rounded-full transition-all duration-300 ease-in-out shadow-xs pointer-events-none"
                :style="positionSliderStyle"
            ></div>

            <!-- Position Buttons -->
            <button
                v-for="position in ENGRAVING_POSITIONS"
                :key="position"
                type="button"
                class="relative flex-1 flex flex-col cursor-pointer items-center justify-center transition-opacity duration-200 z-10 h-full"
                :class="selectedPosition === position ? 'opacity-100' : 'opacity-60 hover:opacity-80'"
                :title="t(pageStr + '.' + positionLabelKeys[position])"
                @click="selectPosition(position)"
            >
              <img
                  :src="ENGRAVING_ASSET_BASE_URL + position + '.svg'"
                  :alt="t(pageStr + '.' + positionLabelKeys[position])"
                  class="h-16 w-16 object-contain pointer-events-none"
              />
              <span class="text-[8px] leading-none pointer-events-none"
                    :class="selectedPosition === position ? 'text-white' : 'text-gray-600'">{{
                  t(pageStr + '.' + positionLabelKeys[position])
                }}</span>
            </button>
          </div>

          <!-- engraving type sub-selector, nested below the currently selected position -->
          <div v-if="engravingTypes.length > 1" class="relative w-full mt-2">
            <!-- connector arrow linking the sub-selector to the active position button -->
            <div
                class="absolute -top-[7px] w-3 h-3 bg-[#f3f0e9] border-l border-t border-[#e8e2d8] rotate-45 z-20 transition-all duration-300 ease-in-out pointer-events-none"
                :style="positionCaretStyle"
            ></div>

            <div
                class="relative flex items-center w-full h-20 bg-[#f3f0e9] rounded-2xl p-[2px] border border-[#e8e2d8] select-none">
              <!-- Sliding Background -->
              <div
                  class="absolute top-[2px] bottom-[2px] bg-brand-main rounded-xl transition-all duration-300 ease-in-out shadow-xs pointer-events-none"
                  :style="typeSliderStyle"
              ></div>

              <!-- Type Buttons -->
              <button
                  v-for="type in engravingTypes"
                  :key="type"
                  type="button"
                  class="relative flex-1 flex flex-col items-center justify-center cursor-pointer transition-opacity duration-200 z-10 h-full"
                  :class="selectedType === type ? 'opacity-100' : 'opacity-60 hover:opacity-80'"
                  :title="t(pageStr + '.' + typeLabelKeys[type])"
                  @click="selectType(type)"
                  @mouseenter="setHoveredType(type)"
                  @mouseleave="clearHoveredType()">
                <img
                    :src="ENGRAVING_ASSET_BASE_URL + tipTypeKeys[type] + '.svg'"
                    :alt="t(pageStr + '.' + typeLabelKeys[type])"
                    class="h-10 w-10 object-contain pointer-events-none"
                />
                <span
                    class="text-[8px] leading-none overflow-hidden whitespace-nowrap px-1 pointer-events-none"
                    :class="selectedType === type ? 'text-white font-medium' : 'text-gray-600'">{{
                    t(pageStr + '.' + typeLabelKeys[type])
                  }}</span>
              </button>
            </div>
          </div>
        </nav>

        <div v-if="selectedPosition === 'inside'"
             class="w-full aspect-[4/1] overflow-hidden flex items-center justify-center rounded-[11px]">
          <item-engraving :engraving="engraving" :main-metal="mainMetal" class="relative"/>
        </div>

        <div v-if="isLaserSelected">
          <input
              type="text"
              v-model="engraving.text"
              ref="textInput"
              @keyup="onTextInput"
              @change="commitEngravingText"
              class="text-base block w-full bg-white p-2 rounded-[11px] border border-brand-main"
              :style="{fontFamily: previewFontFamily}"
          />
        </div>

        <div v-if="isLaserSelected">
          <div class="grid grid-cols-3 gap-2">
            <div
                v-for="[fontIdx, fontName] in focusedFontEntries"
                :key="fontIdx"
                class="cursor-pointer p-2 text-center overflow-hidden  rounded-[11px] border border-brand-main"
                :class="engraving.font === fontIdx ? 'border-brand-main border bg-[#f3f0e9]' : 'border-white bg-white'"
                @click="selectFont(fontIdx)"
                @mouseenter="setHoveredFont(fontIdx)"
                @mouseleave="clearHoveredFont()">
              <span class="block text-sm overflow-hidden whitespace-nowrap"
                    :style="{fontFamily: fontIdx}">{{ engraving.text || 'Aa' }}</span>
              <span class="block text-[10px] text-gray-500 mt-1 overflow-hidden whitespace-nowrap">{{ fontName }}</span>
            </div>
          </div>
          <item-v3-show-more v-if="overflowFontEntries.length > 0">
            <template v-slot:more-label>{{ t(pageStr + '.show_all') }}</template>
            <template v-slot:less-label>{{ t(pageStr + '.show_less') }}</template>
            <div class="grid grid-cols-3 gap-2">
              <div
                  v-for="[fontIdx, fontName] in overflowFontEntries"
                  :key="fontIdx"
                  class="cursor-pointer p-2 text-center overflow-hidden rounded-[11px] border border-brand-main"
                  :class="engraving.font === fontIdx ? 'border-brand-main border bg-[#f3f0e9]' : 'border-white bg-white'"
                  @click="selectFont(fontIdx)"
                  @mouseenter="setHoveredFont(fontIdx)"
                  @mouseleave="clearHoveredFont()">
                <span class="block text-sm overflow-hidden whitespace-nowrap"
                      :style="{fontFamily: fontIdx}">{{ engraving.text || 'Aa' }}</span>
                <span class="block text-[10px]  text-gray-500 mt-1 overflow-hidden whitespace-nowrap">{{
                    fontName
                  }}</span>
              </div>
            </div>
          </item-v3-show-more>
        </div>

        <div v-if="isLaserSelected">
          <div class="grid grid-cols-5 gap-2">
            <div
                v-for="[symboleIdx] in focusedSymbolEntries"
                :key="symboleIdx"
                class="cursor-pointer p-2 bg-white border border-white hover:bg-gray-100 text-center flex flex-col items-center justify-start rounded-[11px] hover:border hover:border-brand-main"
                @click="addSymbole(symboleIdx)">
              <img :src="require('@/assets/img/symbols/' + symboleIdx + '.png')" :alt="symboleIdx" class="w-aut h-6"/>
              <span class="block text-[10px]  text-gray-500 mt-2 overflow-hidden">{{
                  formatSymbolName(symboleIdx)
                }}</span>
            </div>
          </div>
          <item-v3-show-more v-if="overflowSymbolEntries.length > 0">
            <template v-slot:more-label>{{ t(pageStr + '.show_all') }}</template>
            <template v-slot:less-label>{{ t(pageStr + '.show_less') }}</template>
            <div class="grid grid-cols-5 gap-2">
              <div
                  v-for="[symboleIdx] in overflowSymbolEntries"
                  :key="symboleIdx"
                  class="cursor-pointer p-2 bg-white border border-white hover:bg-gray-100 text-center flex flex-col items-center justify-center rounded-[11px] hover:border hover:border-brand-main"
                  @click="addSymbole(symboleIdx)">
                <img :src="require('@/assets/img/symbols/' + symboleIdx + '.png')" :alt="symboleIdx" class="w-8 h-auto"/>
                <span class="block text-[10px]  text-gray-500 mt-1 overflow-hidden">{{
                    formatSymbolName(symboleIdx)
                  }}</span>
              </div>
            </div>
          </item-v3-show-more>
        </div>
      </div>
    </template>
  </item-v3-parameter-box>
</template>

<style scoped>

</style>
